import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getChallengePool } from '../../api/owner'
import { Spinner } from '../../components/icons'
import { kindLabel } from './kind'

/**
 * Ronde 22B en 22H — kiezen uit de pool.
 *
 * Op mobiel een volledig scherm, op desktop een dialoog. Wat al in de lijst
 * staat blijft zichtbaar met een vinkje in plaats van te verdwijnen: anders
 * verspringt de lijst onder je vinger terwijl je aan het kiezen bent.
 */
export function ChallengePoolDialog({
  open,
  taken,
  busy,
  onClose,
  onAdd,
  onCustom,
}: {
  open: boolean

  /** Titels die al in de lijst staan; die kan je niet nog eens toevoegen. */
  taken: string[]
  busy: boolean
  onClose: () => void
  onAdd: (suggestionIds: string[]) => void
  onCustom: () => void
}) {
  const pool = useQuery({ queryKey: ['challenge-pool'], queryFn: getChallengePool, enabled: open })

  const [category, setCategory] = useState<string | null>(null)
  const [picked, setPicked] = useState<string[]>([])

  if (!open) {
    return null
  }

  const categories = pool.data?.categories ?? []
  const active = categories.find((entry) => entry.id === category) ?? categories[0]

  const already = new Set(taken.map((title) => title.toLowerCase()))

  const toggle = (id: string) =>
    setPicked((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    )

  return (
    <div className="fixed inset-0 z-50 flex lg:items-center lg:justify-center lg:bg-[var(--scrim)] lg:p-8">
      <div className="bg-shell lg:rounded-dialog lg:shadow-dialog flex min-h-full w-full flex-col lg:max-h-[86vh] lg:min-h-0 lg:w-[680px]">
        <header className="border-line-soft flex shrink-0 items-start justify-between gap-4 border-b px-5.5 pt-5.5 pb-4 lg:px-7 lg:pt-6">
          <div>
            <p className="font-display text-[26px] leading-tight lg:text-[30px]">Uit de pool</p>
            <p className="text-ink-soft mt-1 text-[13px] lg:text-sm">
              {picked.length === 0
                ? 'Kies wat bij jullie feest past.'
                : `${picked.length} gekozen · je kan ze daarna nog aanpassen`}
            </p>
          </div>

          <button
            type="button"
            aria-label="Sluiten"
            onClick={onClose}
            className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink flex size-9 shrink-0 items-center justify-center rounded-full border transition-colors"
          >
            ✕
          </button>
        </header>

        {pool.isPending ? (
          <div className="flex flex-1 items-center justify-center py-20">
            <Spinner className="text-accent size-6" />
          </div>
        ) : (
          <>
            <div className="scroll-x-clean border-line-soft flex shrink-0 gap-2 border-b px-5.5 py-3 lg:px-7">
              {categories.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => setCategory(entry.id)}
                  aria-pressed={entry.id === active?.id}
                  className={
                    'shrink-0 rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ' +
                    (entry.id === active?.id
                      ? 'bg-ink text-shell font-medium'
                      : 'border-line text-ink-muted hover:bg-tint/50 hover:text-ink border')
                  }
                >
                  {entry.label}
                </button>
              ))}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5.5 py-4 lg:px-7">
              <div className="flex flex-col gap-2">
                {active?.suggestions.map((suggestion) => {
                  const used = already.has(suggestion.title.toLowerCase())
                  const chosen = picked.includes(suggestion.id)

                  return (
                    <button
                      key={suggestion.id}
                      type="button"
                      disabled={used}
                      onClick={() => toggle(suggestion.id)}
                      className={
                        'rounded-notice flex w-full items-center gap-3.5 border p-3.5 text-left transition-colors ' +
                        (used
                          ? 'border-line-card bg-surface opacity-55'
                          : chosen
                            ? 'border-accent bg-tint/40'
                            : 'border-line-card bg-surface hover:border-line-warm hover:bg-notice')
                      }
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-[15px] font-medium">{suggestion.title}</span>
                        <span className="text-ink-faint mt-0.5 block text-[13px]">
                          {kindLabel(suggestion.kind)}
                          {suggestion.suggestedTime && ' · met moment'}
                        </span>
                      </span>

                      <span
                        aria-hidden="true"
                        className={
                          'flex size-7 shrink-0 items-center justify-center rounded-full text-[13px] ' +
                          (used || chosen
                            ? 'bg-accent text-accent-ink'
                            : 'border-line-warm text-ink-soft border')
                        }
                      >
                        {used || chosen ? '✓' : '＋'}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            <footer className="border-line-soft flex shrink-0 items-center gap-3 border-t px-5.5 pt-4 pb-7.5 lg:px-7 lg:py-5">
              <button
                type="button"
                onClick={onCustom}
                className="text-ink-soft hover:text-ink mr-auto hidden text-[15px] transition-colors lg:block"
              >
                Eigen challenge nodig? <span className="text-accent">Zelf maken</span>
              </button>

              <button
                type="button"
                onClick={onClose}
                className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink h-13 shrink-0 basis-29 rounded-full border text-base transition-colors lg:h-12 lg:basis-auto lg:px-6 lg:text-[15px]"
              >
                Annuleren
              </button>

              <button
                type="button"
                disabled={picked.length === 0 || busy}
                onClick={() => onAdd(picked)}
                className="bg-accent text-accent-ink disabled:bg-inert disabled:text-inert-ink h-13 flex-1 rounded-full text-base font-semibold transition hover:brightness-95 disabled:hover:brightness-100 lg:h-12 lg:flex-none lg:px-7.5 lg:text-[15px]"
              >
                {busy ? 'Bezig…' : `${picked.length} toevoegen`}
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  )
}
